import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  FaEnvelope,
  FaServer,
  FaKey,
  FaCheckCircle,
  FaExclamationTriangle,
  FaDesktop,
  FaMobileAlt,
  FaGlobe,
  FaArrowRight,
} from "react-icons/fa";
import "./EmailConfigGuide.css";

const EmailConfigGuide = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="email-config-page">
      {/* Hero Section */}
      <section className="email-hero">
        <div className="email-hero-content">
          <h1>Nastavenie e-mailu</h1>
          <p>
            Kompletný návod na nastavenie vášho INVER e-mailového účtu v Outlooku, Thunderbirde, iPhone/iPad a ďalších štandardných poštových klientoch.
          </p>
        </div>
      </section>

      {/* Quick Server Settings */}
      <section className="settings-section">
        <div className="container">
          <h2>Rýchle nastavenie servera</h2>
          <p className="section-intro">
            Tieto hodnoty použite pri ručnom nastavení e-mailového účtu v akomkoľvek poštovom kliente.
          </p>

          <div className="settings-grid">
            {/* IMAP Card */}
            <div className="setting-card recommended">
              <div className="card-header">
                <FaServer />
                <h3>IMAP (odporúčané)</h3>
                <span className="badge">Odporúčané</span>
              </div>
              <p className="card-description">
                Správy zostávajú synchronizované medzi počítačom, telefónom a webmailom.
              </p>
              <div className="setting-row">
                <span className="label">Server:</span>
                <code className="value">m.inver.sk</code>
              </div>
              <div className="setting-row">
                <span className="label">Port:</span>
                <code className="value">993</code>
              </div>
              <div className="setting-row">
                <span className="label">Zabezpečenie:</span>
                <code className="value">SSL/TLS</code>
              </div>
              <div className="setting-row">
                <span className="label">Autentifikácia:</span>
                <span className="value yes">Áno</span>
              </div>
            </div>

            {/* POP3 Card */}
            <div className="setting-card alternative">
              <div className="card-header">
                <FaServer />
                <h3>POP3 (alternatíva)</h3>
              </div>
              <p className="card-description">
                Správy sa stiahnu na zariadenie. Staršia alternatívna metóda pre jednoduché čítanie pošty.
              </p>
              <div className="setting-row">
                <span className="label">Server:</span>
                <code className="value">m.inver.sk</code>
              </div>
              <div className="setting-row">
                <span className="label">Port:</span>
                <code className="value">995</code>
              </div>
              <div className="setting-row">
                <span className="label">Zabezpečenie:</span>
                <code className="value">SSL/TLS</code>
              </div>
              <div className="setting-row">
                <span className="label">Autentifikácia:</span>
                <span className="value yes">Áno</span>
              </div>
            </div>

            {/* SMTP Card */}
            <div className="setting-card smtp">
              <div className="card-header">
                <FaEnvelope />
                <h3>SMTP (odosielanie)</h3>
              </div>
              <p className="card-description">
                Používa sa na odosielanie e-mailov. Vyžaduje si autentifikáciu.
              </p>
              <div className="setting-row">
                <span className="label">Server:</span>
                <code className="value">m.inver.sk</code>
              </div>
              <div className="setting-row">
                <span className="label">Port:</span>
                <code className="value">465</code>
              </div>
              <div className="setting-row">
                <span className="label">Zabezpečenie:</span>
                <code className="value">SSL/TLS</code>
              </div>
              <div className="setting-row">
                <span className="label">Autentifikácia:</span>
                <span className="value yes required">Povinná</span>
              </div>
            </div>
          </div>

          {/* Credentials Info */}
          <div className="credentials-box">
            <h3><FaKey /> Prihlasovacie údaje</h3>
            <div className="credential-row">
              <span className="label"><strong>Používateľské meno:</strong></span>
              <span className="value">Vaša kompletná e-mailová adresa (napr. pouzivatel@domena.sk)</span>
            </div>
            <div className="credential-row">
              <span className="label"><strong>Heslo:</strong></span>
              <span className="value">Heslo vašej poštovej schránky</span>
            </div>
          </div>
        </div>
      </section>

      {/* Webmail Section */}
      <section className="webmail-section">
        <div className="container">
          <h2><FaGlobe /> Webmail a správa účtu</h2>

          <div className="webmail-cards">
            <div className="webmail-card primary">
              <h3>Hlavný webmail</h3>
              <a href="https://m.inver.sk/" target="_blank" rel="noopener noreferrer" className="webmail-link">
                https://m.inver.sk/ <FaArrowRight />
              </a>
              <p>Použite na prístup k e-mailom a správu vášho účtu.</p>
            </div>

            <div className="webmail-card alternative">
              <h3>Alternatívny webmail</h3>
              <a href="https://webmail.inver.sk/" target="_blank" rel="noopener noreferrer" className="webmail-link">
                https://webmail.inver.sk/ <FaArrowRight />
              </a>
              <p>Alternatívne rozhranie webmailu s iným dizajnom.</p>
            </div>
          </div>

          <div className="account-management">
            <h3>Správa účtu v webmaili</h3>
            <ul className="management-list">
              <li><FaCheckCircle /> Zmena hesla</li>
              <li><FaCheckCircle /> Konfigurácia filtrov pre presun/spracovanie pošty (Nastavenia → Mail → Filters)</li>
              <li><FaCheckCircle /> Nastavenie preposielania podľa vašich pravidiel</li>
              <li><FaCheckCircle /> Automatické odpovede na dovolenke (Nastavenia → Mail → Vacation)</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Outlook Classic Section */}
      <section className="client-section">
        <div className="container">
          <h2><FaDesktop /> Microsoft Outlook Classic (Windows)</h2>

          <ol className="steps-list">
            <li>Otvorte Outlook a prejdite do nastavení účtov.</li>
            <li>Kliknite na "Pridať účet" alebo "Add Account".</li>
            <li>Zadajte svoju kompletnú e-mailovú adresu (napr. pouzivatel@domena.sk).</li>
            <li>Ak je k dispozícii, vyberte možnosť manuálnej/rozšírenej konfigurácie.</li>
            <li>Vyberte protokol IMAP.</li>

            <li className="step-sub">
              <strong>Nastavenie prichádzajúcej pošty (IMAP):</strong>
              <ul>
                <li>Server: <code>m.inver.sk</code></li>
                <li>Port: <code>993</code></li>
                <li>Zabezpečenie: <code>SSL/TLS</code></li>
              </ul>
            </li>

            <li className="step-sub">
              <strong>Nastavenie odchádzajúcej pošty (SMTP):</strong>
              <ul>
                <li>Server: <code>m.inver.sk</code></li>
                <li>Port: <code>465</code></li>
                <li>Zabezpečenie: <code>SSL/TLS</code></li>
                <li>Označte "Odchodový server vyžaduje autentifikáciu"</li>
              </ul>
            </li>

            <li>Používateľské meno: vaša kompletná e-mailová adresa.</li>
            <li>Zadajte heslo vašej poštovej schránky.</li>
            <li>Dokončite nastavenie a otestujte účet.</li>
          </ol>

          <div className="note-box">
            <FaExclamationTriangle />
            <p>Poznámka: Presné názvy položiek v menu sa môžu mierne líšiť podľa verzie Outlooku/Office.</p>
          </div>
        </div>
      </section>

      {/* New Outlook Section */}
      <section className="client-section">
        <div className="container">
          <h2><FaDesktop /> Nový Outlook pre Windows</h2>

          <ol className="steps-list">
            <li>Otvorte nový Outlook a prejdite do Nastavení (Settings).</li>
            <li>Vyberte správu účtov (Accounts).</li>
            <li>Kliknite na "Pridať účet" (Add account).</li>
            <li>Zadajte svoju kompletnú e-mailovú adresu.</li>
            <li>Ak automatické nastavenie nefunguje, vyberte manuálnu IMAP konfiguráciu.</li>

            <li className="step-sub">
              <strong>Nastavenie prichádzajúcej pošty (IMAP):</strong>
              <ul>
                <li>Server: <code>m.inver.sk</code></li>
                <li>Port: <code>993</code></li>
                <li>Zabezpečenie: <code>SSL/TLS</code></li>
              </ul>
            </li>

            <li className="step-sub">
              <strong>Nastavenie odchádzajúcej pošty (SMTP):</strong>
              <ul>
                <li>Server: <code>m.inver.sk</code></li>
                <li>Port: <code>465</code></li>
                <li>Zabezpečenie: <code>SSL/TLS</code></li>
                <li>Autentifikácia s používateľským menom a heslom</li>
              </ul>
            </li>

            <li>Používateľské meno: vaša kompletná e-mailová adresa.</li>
            <li>Zadajte heslo vašej poštovej schránky.</li>
          </ol>

          <div className="note-box">
            <FaExclamationTriangle />
            <p>Poznámka: Microsoft môže meniť presné názvy a umiestnenie položiek medzi verziami Nového Outlooku.</p>
          </div>
        </div>
      </section>

      {/* Thunderbird Section */}
      <section className="client-section">
        <div className="container">
          <h2><FaEnvelope /> Mozilla Thunderbird</h2>

          <ol className="steps-list">
            <li>Otvorte Thunderbird a kliknite na "Pridať existujúci e-mailový účet".</li>
            <li>Zadajte svoje meno, kompletnú e-mailovú adresu a heslo.</li>
            <li>Ak je potrebné, vyberte "Manuálna konfigurácia" (Manual Configuration).</li>

            <li className="step-sub">
              <strong>Nastavenie prichádzajúcej pošty:</strong>
              <ul>
                <li>Protokol: IMAP</li>
                <li>Hostname: <code>m.inver.sk</code></li>
                <li>Port: <code>993</code></li>
                <li>Zabezpečenie: SSL/TLS</li>
              </ul>
            </li>

            <li className="step-sub">
              <strong>Nastavenie odchádzajúcej pošty (SMTP):</strong>
              <ul>
                <li>Hostname: <code>m.inver.sk</code></li>
                <li>Port: <code>465</code></li>
                <li>Zabezpečenie: SSL/TLS</li>
                <li>Autentifikácia s používateľským menom a heslom poštovej schránky</li>
              </ul>
            </li>

            <li>Používateľské meno: vaša kompletná e-mailová adresa.</li>
            <li>Uložte nastavenia a otestujte účet.</li>
          </ol>
        </div>
      </section>

      {/* Apple Mail Section */}
      <section className="client-section">
        <div className="container">
          <h2><FaMobileAlt /> Apple Mail na iPhone/iPad</h2>

          <ol className="steps-list">
            <li>Otvorte Nastavenia (Settings) na vašom zariadení.</li>
            <li>Prejdite do Mail → Kontá → Pridať účet.</li>
            <li>Vyberte možnosť pre iného/vlastného poskytovateľa a použite IMAP.</li>

            <li className="step-sub">
              <strong>Nastavenie prichádzajúcej pošty:</strong>
              <ul>
                <li>Hostname: <code>m.inver.sk</code></li>
                <li>Používateľské meno: vaša kompletná e-mailová adresa</li>
                <li>Heslo: heslo vašej poštovej schránky</li>
              </ul>
            </li>

            <li className="step-sub">
              <strong>Nastavenie odchádzajúcej pošty:</strong>
              <ul>
                <li>Hostname: <code>m.inver.sk</code></li>
                <li>Používateľské meno: vaša kompletná e-mailová adresa</li>
                <li>Heslo: heslo vašej poštovej schránky</li>
              </ul>
            </li>

            <li className="step-sub">
              <strong>Zabezpečenie:</strong>
              <ul>
                <li>IMAP: port 993, SSL/TLS</li>
                <li>SMTP: port 465, SSL/TLS</li>
              </ul>
            </li>

            <li>Uložte nastavenia a otestujte účet.</li>
          </ol>

          <div className="note-box">
            <FaExclamationTriangle />
            <p>Poznámka: Názvy položiek v menu sa môžu mierne líšiť podľa verzie iOS.</p>
          </div>
        </div>
      </section>

      {/* Troubleshooting Section */}
      <section className="troubleshoot-section">
        <div className="container">
          <h2><FaExclamationTriangle /> Riešenie problémov</h2>

          <div className="trouble-cards">
            <div className="trouble-card">
              <h3>Nemôžem prijímať poštu</h3>
              <p>Kontrolujte:</p>
              <ul>
                <li>IMAP server: <code>m.inver.sk</code></li>
                <li>Port: <code>993</code></li>
                <li>Zabezpečenie: SSL/TLS</li>
                <li>Používateľské meno: kompletná e-mailová adresa</li>
                <li>Heslo je správne</li>
              </ul>
            </div>

            <div className="trouble-card">
              <h3>Môžem prijímať, ale nie odosielať poštu</h3>
              <p>Kontrolujte:</p>
              <ul>
                <li>SMTP server: <code>m.inver.sk</code></li>
                <li>Port: <code>465</code></li>
                <li>Zabezpečenie: SSL/TLS</li>
                <li>Autentifikácia SMTP je zapnutá</li>
                <li>Používateľské meno: kompletná e-mailová adresa</li>
                <li>Heslo je správne</li>
              </ul>
            </div>

            <div className="trouble-card">
              <h3>Prihlásenie nefunguje</h3>
              <p>Otestujte svoje prihlasovacie údaje cez webmail:</p>
              <a href="https://m.inver.sk/" target="_blank" rel="noopener noreferrer" className="webmail-link">
                https://m.inver.sk/ <FaArrowRight />
              </a>
              <p>Ak sa neprihlásite ani v webmaili, problém pravdepodobne nie je v nastavení vášho poštového klienta.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Back to Support */}
      <section className="back-section">
        <div className="container">
          <Link to="/podpora" className="back-link">
            ← Späť na stránku podpory
          </Link>
        </div>
      </section>
    </div>
  );
};

export default EmailConfigGuide;
